import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

type Stage = 'loading' | 'form' | 'done' | 'error';

interface Profession {
  professionID: string;
  name: string;
  isActive?: boolean;
}

const InviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('loading');
  const [companyName, setCompanyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isManaged, setIsManaged] = useState(false);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirm: '',
    professionID: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/api/company/accept/${token}`),
      api.get('/api/professions').catch(() => ({ data: [] })),
    ])
      .then(([r, pRes]) => {
        setCompanyName(r.data.companyName);
        setInviteEmail(r.data.email);
        setIsManaged(!!r.data.isManaged);
        setProfessions((pRes.data || []).filter((p: Profession) => p.isActive !== false));
        if (r.data.isManaged) {
          setForm(f => ({ ...f, firstName: r.data.firstName || '', lastName: r.data.lastName || '' }));
        }
        setStage('form');
      })
      .catch(e => {
        setError(e.response?.data?.error || 'This invite link is invalid or has already been used.');
        setStage('error');
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post(`/api/company/accept/${token}/register`, {
        firstName: form.firstName,
        lastName: form.lastName,
        password: form.password,
        professionID: form.professionID || undefined,
      });
      setStage('done');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === 'loading') {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <div className="card shadow-sm border-0">
          <div className="card-body p-5 text-center">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
            <h5 className="fw-bold mb-3">Invite Invalid</h5>
            <p className="text-muted mb-4">{error}</p>
            <button className="btn btn-outline-primary" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <div className="card shadow-sm border-0">
          <div className="card-body p-5 text-center">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
            <h5 className="fw-bold mb-2">
              {isManaged ? 'Password set!' : `Welcome to ${companyName}!`}
            </h5>
            <p className="text-muted mb-4">
              {isManaged
                ? 'Your password has been set. Log in with your email and new password.'
                : 'Your account has been created and linked to the team. Log in to get started.'}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>
              Log In Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5" style={{ maxWidth: 480 }}>
      <div className="card shadow-sm border-0">
        <div className="card-body p-5">
          <div className="text-center mb-4">
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👥</div>
            <h4 className="fw-bold mb-1">{isManaged ? `Set Your Password` : `Join ${companyName}`}</h4>
            <p className="text-muted small mb-0">
              {isManaged
                ? `Your account at ${companyName} is ready. Set a password to log in.`
                : `You've been invited to ${companyName} on CEC Track. Create your account to accept.`}
            </p>
          </div>

          {error && <div className="alert alert-danger py-2 small">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label small fw-semibold">Email</label>
              <input
                className="form-control"
                value={inviteEmail}
                disabled
                style={{ background: '#f8f9fa', color: '#6c757d' }}
              />
            </div>

            <div className="row g-2 mb-3">
              <div className="col">
                <label className="form-label small fw-semibold">First Name</label>
                <input
                  className="form-control"
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  required
                  autoFocus={!isManaged}
                />
              </div>
              <div className="col">
                <label className="form-label small fw-semibold">Last Name</label>
                <input
                  className="form-control"
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            {professions.length > 0 && (
              <div className="mb-3">
                <label className="form-label small fw-semibold">
                  Profession <span className="text-muted fw-normal">(optional — you can set this later)</span>
                </label>
                <select
                  className="form-select"
                  value={form.professionID}
                  onChange={e => setForm(f => ({ ...f, professionID: e.target.value }))}
                >
                  <option value="">Select your profession</option>
                  {professions.map(p => (
                    <option key={p.professionID} value={p.professionID}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label small fw-semibold">Password</label>
              <input
                type="password"
                className="form-control"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required
                minLength={8}
                placeholder="At least 8 characters"
                autoFocus={isManaged}
              />
            </div>

            <div className="mb-4">
              <label className="form-label small fw-semibold">Confirm Password</label>
              <input
                type="password"
                className="form-control"
                value={form.confirm}
                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={submitting}
            >
              {submitting
                ? (isManaged ? 'Saving…' : 'Creating Account…')
                : (isManaged ? 'Set Password & Log In' : 'Create Account & Join Team')}
            </button>
          </form>

          {!isManaged && (
            <p className="text-center text-muted small mt-3 mb-0">
              Already have an account?{' '}
              <a href="/login" className="text-primary">Log in</a> — your account will be linked automatically.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
